/**
 * ProposalCard — assistant-proposed prompt diff with action CTAs (Story 8.5
 * AC2 + AC3).
 *
 * Layout
 * ------
 *   - Collapsed:  one-line summary "N mudanças propostas" + chevron toggle.
 *   - Expanded:   for each `ProposedChange`:
 *                   - section heading
 *                   - rationale (italic)
 *                   - side-by-side `before` / `after` text panels
 *
 * Actions (RBAC-aware)
 * --------------------
 *   - "Aplicar e ativar"   — admin only (raises `ACTIVATION_REQUIRES_ADMIN`
 *                            on operator if attempted; UI hides the button)
 *   - "Aplicar (sem ativar)" — operator+
 *   - "Descartar"          — operator+
 *
 * The card is a *presentational* component — it never calls the API. The
 * parent page wires `onApply(activate)` / `onDiscard()` to mutations and
 * passes a `pending` flag to disable the buttons during in-flight requests.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ProposedChange, ProposedCodeChange } from '@/lib/promptAssistant';

export interface ProposalCardProps {
  /** Operator-readable analysis from the assistant. */
  analysis: string;
  /** Concrete prompt diffs the assistant proposed. */
  proposedChanges: ProposedChange[];
  /** Optional code-level recommendations (descriptive only). */
  proposedCodeChanges?: ProposedCodeChange[];
  /** Current actor role — gates which CTAs render. */
  role: 'viewer' | 'operator' | 'admin';
  /** True when an apply / discard mutation is in flight. */
  pending?: boolean;
  /** True when the parent session is no longer `open`. */
  alreadyResolved?: boolean;
  onApply: (activate: boolean) => void;
  onDiscard: () => void;
}

export function ProposalCard({
  analysis,
  proposedChanges,
  proposedCodeChanges = [],
  role,
  pending = false,
  alreadyResolved = false,
  onApply,
  onDiscard,
}: ProposalCardProps) {
  const [expanded, setExpanded] = useState(true);

  const canMutate = role === 'operator' || role === 'admin';
  const canActivate = role === 'admin';

  if (proposedChanges.length === 0) {
    return (
      <Card data-testid="proposal-card-empty">
        <CardContent className="py-3 text-xs text-muted-foreground">
          O assistente respondeu apenas com análise — nenhuma mudança concreta proposta.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="proposal-card">
      <CardHeader className="cursor-pointer pb-2" onClick={() => setExpanded((v) => !v)}>
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown aria-hidden className="h-4 w-4" />
            ) : (
              <ChevronRight aria-hidden className="h-4 w-4" />
            )}
            Proposta — {proposedChanges.length} mudança{proposedChanges.length === 1 ? '' : 's'}
            {proposedCodeChanges.length > 0 ? (
              <span className="text-xs font-normal text-muted-foreground">
                · {proposedCodeChanges.length} sugestão de código
              </span>
            ) : null}
          </span>
        </CardTitle>
      </CardHeader>

      {expanded ? (
        <CardContent className="space-y-4">
          {analysis ? (
            <div className="rounded bg-muted/50 px-3 py-2 text-xs italic text-muted-foreground">
              {analysis}
            </div>
          ) : null}

          {proposedChanges.map((change, idx) => (
            <div key={`${change.section}-${idx}`} className="space-y-2 rounded border p-3">
              <div className="text-sm font-semibold">{change.section}</div>
              {change.rationale ? (
                <div className="text-xs italic text-muted-foreground">{change.rationale}</div>
              ) : null}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
                    Antes
                  </div>
                  <pre className="whitespace-pre-wrap rounded border border-destructive/30 bg-destructive/5 p-2 text-xs">
                    {change.before || <span className="text-muted-foreground">(vazio)</span>}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
                    Depois
                  </div>
                  <pre className="whitespace-pre-wrap rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs">
                    {change.after || <span className="text-muted-foreground">(vazio)</span>}
                  </pre>
                </div>
              </div>
            </div>
          ))}

          {proposedCodeChanges.length > 0 ? (
            <details className="rounded border p-2 text-xs">
              <summary className="cursor-pointer font-medium">
                Sugestões de código (não aplicadas) — {proposedCodeChanges.length}
              </summary>
              <ul className="mt-2 space-y-1 pl-4">
                {proposedCodeChanges.map((c, idx) => (
                  <li key={idx} className="list-disc">
                    <code className="font-mono text-[11px]">{c.file}</code> — {c.change}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            {alreadyResolved ? (
              <span className="text-xs italic text-muted-foreground">
                Esta sessão já foi resolvida — ações desabilitadas.
              </span>
            ) : !canMutate ? (
              <span className="text-xs italic text-muted-foreground">
                Acesso somente leitura — operator/admin necessários para aplicar.
              </span>
            ) : (
              <>
                {canActivate ? (
                  <Button
                    type="button"
                    onClick={() => onApply(true)}
                    disabled={pending}
                    data-testid="proposal-apply-activate"
                  >
                    Aplicar e ativar
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onApply(false)}
                  disabled={pending}
                  data-testid="proposal-apply"
                >
                  Aplicar (sem ativar)
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onDiscard}
                  disabled={pending}
                  data-testid="proposal-discard"
                >
                  Descartar
                </Button>
              </>
            )}
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
}
